import { Fragment, type ReactNode } from 'react';
import { cn } from './cn.js';
import { Icon } from './Icon.js';

/**
 * Onde um passo está em relação ao registro: já passou, é onde ele está, ou é
 * um dos próximos possíveis.
 */
export type StepState = 'done' | 'now' | 'next';

/**
 * O nó das trilhas — a de fases, no alto da mesa, e a de cada linha de uma
 * `RelatedItem`. Uma peça só, para todas dizerem a mesma coisa do mesmo jeito:
 * verde cheio com check no que passou, anel na cor de destaque onde está,
 * cinza tracejado no que pode vir.
 *
 * Nasceu na mesa do sinistro (29/09/2026), onde a primeira versão era uma
 * pílula com um check pequeno — e ela não se lia como "concluída". O nó cheio
 * se lê.
 */
export function StepNode({ state }: { state: StepState }) {
  return (
    <span
      aria-hidden
      className={cn(
        'flex size-5 shrink-0 items-center justify-center rounded-full',
        state === 'done' && 'bg-sucesso text-texto-inverso',
        state === 'now' && 'border-2 border-primaria bg-superficie',
        state === 'next' && 'border-2 border-dashed border-borda-forte',
      )}
    >
      {state === 'done' && <Icon nome="confirmado" size={16} />}
      {state === 'now' && <span className="size-1.5 rounded-full bg-primaria" />}
    </span>
  );
}

/**
 * O nó com NÚMERO — o título das seções de um formulário em passos, quando a
 * ordem é parte da orientação (o Novo acionamento da Assistência 24h,
 * 02/11/2026). Os três estados são os mesmos do `StepNode`, mas o "agora" é
 * cheio na primária: aqui ele marca a seção que falta preencher, e precisa
 * chamar o olho no meio de quatro cartões.
 *
 * O estado também vai em texto para leitor de tela — a cor e o check são só a
 * metade visível.
 */
export function StepNumber({ n, state }: { n: number; state: StepState }) {
  return (
    <>
      <span
        aria-hidden
        className={cn(
          'flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-bold tabular-nums transition-colors',
          state === 'done' && 'bg-sucesso text-texto-inverso',
          state === 'now' && 'bg-primaria text-primaria-contraste',
          state === 'next' && 'bg-superficie-suave text-texto-suave',
        )}
      >
        {state === 'done' ? <Icon nome="confirmado" size={16} /> : n}
      </span>
      <span className="sr-only">
        {state === 'done' ? 'Completo: ' : state === 'now' ? 'A preencher: ' : ''}
      </span>
    </>
  );
}

export interface TrailStep {
  key: string;
  label: ReactNode;
  state: StepState;
  /**
   * Com a função, o passo vira botão — na mesa, a fase por onde o sinistro
   * passou abre o formulário dela para corrigir. Sem ela, o passo só informa.
   */
  onSelect?: () => void;
  /** O passo que está sendo mostrado agora, quando não é o atual. */
  selected?: boolean;
}

/**
 * A trilha horizontal: o caminho que o registro fez, onde ele está e para
 * onde pode ir.
 *
 * **Não é um passo a passo fixo.** Os fluxos da casa têm desvios, e a trilha
 * mostra o caminho que AQUELE registro fez — quem monta os passos é o módulo,
 * a partir do que está gravado. Passo inventado para completar a linha é a
 * tela afirmando o que o código não cumpre.
 *
 * O traço depois de um passo concluído é verde, para o caminho percorrido se
 * ler de longe; o resto é cinza.
 */
export function StepTrail({
  label,
  steps,
  aside,
}: {
  /** O rótulo de acessibilidade da trilha — "Onde o sinistro está". */
  label: string;
  steps: readonly TrailStep[];
  /** O que vem na ponta direita — uma nota curta, "o caminho ainda não é gravado". */
  aside?: ReactNode;
}) {
  return (
    <nav
      aria-label={label}
      className="flex flex-wrap items-center gap-x-1 gap-y-2 border-b border-borda-suave px-4 py-3"
    >
      <ol className="flex min-w-0 flex-wrap items-center gap-x-1 gap-y-2">
        {steps.map((step, i) => (
          <Fragment key={step.key}>
            {i > 0 && (
              <li
                aria-hidden
                className={cn(
                  'w-4 border-t-2',
                  steps[i - 1]?.state === 'done' ? 'border-sucesso' : 'border-borda',
                )}
              />
            )}
            <li className="min-w-0">
              <TrailStepBody step={step} />
            </li>
          </Fragment>
        ))}
      </ol>
      {aside && <span className="ml-auto text-xs text-texto-tenue">{aside}</span>}
    </nav>
  );
}

function TrailStepBody({ step }: { step: TrailStep }) {
  const body = (
    <>
      <StepNode state={step.state} />
      <span className="min-w-0">{step.label}</span>
    </>
  );
  const classes = cn(
    'flex min-w-0 items-center gap-2 rounded-borda px-1.5 py-1 text-sm',
    step.state === 'now' && 'font-semibold text-texto',
    step.state === 'done' && 'font-semibold text-texto-suave',
    step.state === 'next' && 'text-texto-tenue',
    step.selected && 'bg-sucesso-suave text-texto',
  );
  if (!step.onSelect) {
    return (
      <span aria-current={step.state === 'now' ? 'step' : undefined} className={classes}>
        {body}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={step.onSelect}
      aria-current={step.state === 'now' ? 'step' : undefined}
      aria-pressed={step.selected ?? undefined}
      className={cn(classes, 'text-left hover:bg-superficie-suave active:bg-borda-suave')}
    >
      {body}
    </button>
  );
}

export interface PathStep {
  key: string;
  title: ReactNode;
  /** A segunda linha — a fase, "concluída", a data do marco. */
  detail?: ReactNode;
  state: StepState;
  /**
   * Com a função, a linha inteira vira botão, com a seta à direita. Texto
   * solto não se lia como clicável — medido na mesa do sinistro.
   */
  onSelect?: () => void;
  /**
   * O que o clique abre, por extenso: "Abrir o Reparo do aviso #200". Vira o
   * rótulo de acessibilidade e a dica. Obrigatório com `onSelect`.
   */
  actionLabel?: string;
  /** O passo aberto agora: fundo de destaque, sem seta e sem clique. */
  current?: boolean;
  /** O que vem ao lado do título — "aberta". */
  badge?: ReactNode;
}

/**
 * A trilha vertical: um passo por linha, ligados por um traço.
 *
 * Serve a dois usos com a mesma cara — a mini trilha de departamentos de cada
 * envolvido (cada passo leva a algum lugar) e os marcos de um processo com a
 * data de cada um (nenhum leva a lugar nenhum). Sem `onSelect`, o passo é só
 * leitura e não finge ser botão.
 */
export function StepPath({
  label,
  steps,
}: {
  /** "Por onde o aviso #200 passou". */
  label: string;
  steps: readonly PathStep[];
}) {
  return (
    <ol aria-label={label} className="-ml-1 flex flex-col">
      {steps.map((step, i) => (
        <li key={step.key} className="relative">
          {i > 0 && (
            <span
              aria-hidden
              className={cn(
                'absolute -top-1.5 left-[0.95rem] h-3 border-l-2',
                steps[i - 1]?.state === 'done' ? 'border-sucesso' : 'border-borda',
              )}
            />
          )}
          <PathStepBody step={step} />
        </li>
      ))}
    </ol>
  );
}

function PathStepBody({ step }: { step: PathStep }) {
  const body = (
    <>
      <StepNode state={step.state} />
      <span className="flex min-w-0 flex-1 flex-col leading-tight">
        <span className="flex items-center gap-1.5 text-xs font-semibold">
          {step.title}
          {step.badge}
        </span>
        {step.detail && (
          <span
            className={cn(
              'text-xs break-words',
              step.state === 'done' ? 'text-sucesso' : 'text-texto-suave',
            )}
          >
            {step.detail}
          </span>
        )}
      </span>
    </>
  );
  const classes = 'flex w-full items-center gap-2 rounded-borda px-1.5 py-1 text-left';
  if (!step.onSelect) return <div className={classes}>{body}</div>;
  return (
    <button
      type="button"
      aria-label={step.actionLabel}
      title={step.actionLabel}
      aria-current={step.current || undefined}
      disabled={step.current}
      onClick={step.onSelect}
      className={cn(
        'group',
        classes,
        step.current
          ? 'bg-primaria-suave disabled:cursor-default'
          : 'hover:bg-superficie active:bg-borda-suave',
      )}
    >
      {body}
      {!step.current && (
        <Icon
          nome="seta-direita"
          size={16}
          className="shrink-0 text-texto-tenue group-hover:text-texto"
        />
      )}
    </button>
  );
}
